import React, { useState } from 'react';
import PatientDataUpload from './PatientDataUpload';
import './Patients.css';

const BodyPartDataModal = ({ show, onHide, bodyPart, patient, onDataUploaded }) => {
  const [activeDataType, setActiveDataType] = useState(null);
  const [selectedImagingFormat, setSelectedImagingFormat] = useState(null);

  // Medical imaging format sub-options
  const imagingFormats = [
    {
      key: "nifti",
      label: "NIfTI",
      icon: "fa-brain",
      description: ".nii, .nii.gz files",
      accept: ".nii,.gz",
      noAcceptFilter: true
    },
    {
      key: "dicom",
      label: "DICOM Folder",
      icon: "fa-folder-open",
      description: ".dcm files",
      accept: ".dcm"
    },
    {
      key: "numpy",
      label: "Numpy",
      icon: "fa-cube",
      description: ".npy, .npz files",
      accept: ".npy,.npz"
    }
  ];

  // Data type configurations
  const dataTypes = [
    {
      key: "imaging",
      label: "Medical Imaging",
      icon: "fa-x-ray",
      description: "NIfTI, DICOM, Numpy",
      accept: ".dcm,.nii,.nii.gz,.npy,.npz",
      color: "primary",
      hasSubOptions: true
    },
    {
      key: "image",
      label: "Images",
      icon: "fa-image",
      description: "PNG, JPG, GIF",
      accept: ".png,.jpg,.jpeg,.gif,.bmp,.tiff",
      color: "success"
    },
    {
      key: "video",
      label: "Videos",
      icon: "fa-video-camera",
      description: "MP4, AVI, MOV, MKV, WebM",
      accept: ".mp4,.avi,.mov,.mkv,.wmv,.webm",
      color: "dark"
    },
    {
      key: "document",
      label: "Documents",
      icon: "fa-file-text",
      description: "MD, Word, PDF",
      accept: ".md,.txt,.doc,.docx,.pdf",
      color: "warning"
    },
    {
      key: "table",
      label: "Tables",
      icon: "fa-table",
      description: "Excel, CSV",
      accept: ".xlsx,.xls,.csv",
      color: "info"
    },
    {
      key: "signal",
      label: "Signals",
      icon: "fa-wave-square",
      description: "CSV, EDF",
      accept: ".csv,.edf",
      color: "secondary"
    },
    {
      key: "pathology",
      label: "Pathology",
      icon: "fa-microscope",
      description: "SVS, TIFF, NDPI",
      accept: ".svs,.tiff,.tif,.ndpi",
      color: "danger"
    },
    {
      key: "ehr",
      label: "EHR",
      icon: "fa-heartbeat",
      description: "JSON, CSV",
      accept: ".json,.csv",
      color: "primary"
    }
  ];

  const handleDataUploadedInternal = (newItems) => {
    // Reset state
    setActiveDataType(null);
    setSelectedImagingFormat(null);

    // Call parent callback
    if (onDataUploaded) {
      onDataUploaded(newItems, bodyPart);
    }
  };

  const handleClose = () => {
    setActiveDataType(null);
    setSelectedImagingFormat(null);
    onHide();
  };

  if (!show || !bodyPart) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose}></div>
      <div className="modal show body-part-modal" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog" onClick={handleClose}>
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa fa-database me-2"></i>
              Add Data to Body Part
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-info mb-3">
              <i className="fa fa-info-circle me-2"></i>
              Upload data for the selected location on the body
            </div>

            {/* Data Type Buttons */}
            <div className="data-type-buttons">
              {dataTypes.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  className={`data-type-btn ${activeDataType === type.key ? 'active' : ''} btn-${type.color}`}
                  onClick={() => {
                    if (type.key === activeDataType) {
                      setActiveDataType(null);
                      setSelectedImagingFormat(null);
                    } else {
                      setActiveDataType(type.key);
                      setSelectedImagingFormat(null);
                    }
                  }}
                >
                  <i className={`fa ${type.icon}`}></i>
                  <div className="data-type-info">
                    <span className="data-type-label">{type.label}</span>
                    <span className="data-type-desc">{type.description}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Imaging Format Sub-options */}
            {activeDataType === 'imaging' && (
              <div className="imaging-format-options mt-3">
                <p className="format-label mb-2">Select imaging format:</p>
                <div className="format-buttons">
                  {imagingFormats.map((format) => (
                    <button
                      key={format.key}
                      type="button"
                      className={`format-btn ${selectedImagingFormat === format.key ? 'active' : ''}`}
                      onClick={() => setSelectedImagingFormat(format.key)}
                    >
                      <i className={`fa ${format.icon} me-2`}></i>
                      <div className="format-info">
                        <span className="format-name">{format.label}</span>
                        <span className="format-desc">{format.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Form */}
            {activeDataType && (activeDataType !== 'imaging' || selectedImagingFormat) && (
              <div className="data-upload-form mt-4">
                <PatientDataUpload
                  patient={patient}
                  dataType={activeDataType}
                  imagingFormat={selectedImagingFormat}
                  clickPosition={bodyPart}  // Pass click position
                  acceptedFormats={
                    activeDataType === 'imaging' && selectedImagingFormat
                      ? imagingFormats.find(f => f.key === selectedImagingFormat)?.accept
                      : dataTypes.find(t => t.key === activeDataType)?.accept
                  }
                  noAcceptFilter={
                    activeDataType === 'imaging' && selectedImagingFormat
                      ? imagingFormats.find(f => f.key === selectedImagingFormat)?.noAcceptFilter
                      : false
                  }
                  onDataUploaded={handleDataUploadedInternal}
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BodyPartDataModal;
